import { useLayoutEffect } from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';

/**
 * Legacy redirects when edge _redirects do not run (Cloudflare Workers).
 * Keep in sync with workers/legacy-pro-redirects.mjs.
 *
 * /login → /customer/login (golfer / Core). Pro sign-in is /pro/login.
 */
const ALL_LEGACY_REDIRECTS: [string, string][] = [
  ['/login', '/customer/login'],
  ['/register', '/pro/register'],
  ['/forgot-password', '/pro/forgot-password'],
  ['/reset-password', '/pro/reset-password'],
  ['/set-password', '/pro/set-password'],
  ['/subscription-required', '/pro/subscription-required'],
  ['/subscription-success', '/pro/subscription-success'],
  ['/approval-required', '/pro/approval-required'],
  ['/select-golfer', '/pro/select-golfer'],
  ['/fitting/configure', '/pro/fitting/configure'],
  ['/fitting', '/pro/fitting'],
  ['/configurations', '/pro/configurations'],
  ['/saved-configurations', '/pro/saved-configurations'],
  ['/create-offer', '/pro/create-offer'],
  ['/customer-offer', '/pro/customer-offer'],
  ['/cart', '/pro/cart'],
  ['/profile', '/pro/profile'],
  ['/checkout', '/pro/checkout'],
  ['/admin', '/pro/admin'],
  ['/account-locked', '/pro/account-locked'],
];

const LEGACY_PATH_MAP = new Map(ALL_LEGACY_REDIRECTS);

/** Normalize /login/ → /login before lookup. */
export function resolveLegacyProRedirect(pathname: string): string | null {
  const path =
    pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return LEGACY_PATH_MAP.get(path) ?? null;
}

/**
 * Redirect legacy paths before route matching. Uses location.replace so
 * /login/ cannot flash NotFound when React Router misses the trailing slash.
 */
export function LegacyProPathGuard() {
  const { pathname, search, hash } = useLocation();
  const to = resolveLegacyProRedirect(pathname);

  useLayoutEffect(() => {
    if (!to) return;
    window.location.replace(`${to}${search}${hash}`);
  }, [to, search, hash]);

  if (!to) return null;
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function LegacyProRedirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

/** Place before the catch-all * route inside the layout <Route path="/">. */
export function legacyProRedirectRoutes() {
  return ALL_LEGACY_REDIRECTS.flatMap(([from, to]) => {
    const segment = from.slice(1);
    return [
      <Route key={from} path={segment} element={<LegacyProRedirect to={to} />} />,
      <Route key={`${from}/`} path={`${segment}/`} element={<LegacyProRedirect to={to} />} />,
    ];
  });
}
